'use client';

import React from 'react';
import {
  Zap, Eye, Type, Star, DollarSign, Smartphone,
  FileText, ShieldCheck, Search, Layout, Loader2,
  CheckCircle2, AlertTriangle, XCircle, Clock
} from 'lucide-react';
import { CheckResult } from '../lib/auditEngine';

const CHECK_META: Record<string, { label: string; icon: React.ElementType; description: string }> = {
  'load-time': {
    label: 'Page Load Speed',
    icon: Zap,
    description: 'Time to first meaningful paint and interactive',
  },
  'cta-above-fold': {
    label: 'CTA Above the Fold',
    icon: Eye,
    description: 'Primary call-to-action visible without scrolling',
  },
  'headline-clarity': {
    label: 'Headline Clarity',
    icon: Type,
    description: 'Value proposition communicated in the hero headline',
  },
  'social-proof': {
    label: 'Social Proof Signals',
    icon: Star,
    description: 'Testimonials, logos, reviews, or user counts',
  },
  'pricing-clarity': {
    label: 'Pricing Transparency',
    icon: DollarSign,
    description: 'Pricing visible, clear tiers, no hidden cost friction',
  },
  'mobile-responsive': {
    label: 'Mobile Responsiveness',
    icon: Smartphone,
    description: 'Viewport meta tag, responsive layout signals',
  },
  'form-friction': {
    label: 'Signup Form Friction',
    icon: FileText,
    description: 'Form field count, required fields, friction indicators',
  },
  'trust-signals': {
    label: 'Trust & Security Signals',
    icon: ShieldCheck,
    description: 'HTTPS, privacy policy, security badges, guarantees',
  },
  'seo-meta': {
    label: 'SEO & Meta Tags',
    icon: Search,
    description: 'Title, description, OG tags, canonical URL',
  },
  'page-structure': {
    label: 'Page Structure & Flow',
    icon: Layout,
    description: 'Logical content hierarchy, section ordering',
  },
};

const SEVERITY_CONFIG = {
  critical: {
    label: 'Critical',
    color: 'var(--severity-critical)',
    bg: 'var(--severity-critical-bg)',
    border: 'rgba(255,61,87,0.25)',
    icon: XCircle,
  },
  warning: {
    label: 'Warning',
    color: 'var(--severity-warning)',
    bg: 'var(--severity-warning-bg)',
    border: 'rgba(255,190,61,0.25)',
    icon: AlertTriangle,
  },
  pass: {
    label: 'Pass',
    color: 'var(--severity-pass)',
    bg: 'var(--severity-pass-bg)',
    border: 'rgba(61,255,143,0.2)',
    icon: CheckCircle2,
  },
};

interface ChecksStreamProps {
  streamedCheckIds: string[];
  allCheckIds: string[];
  checks?: CheckResult[];
  isScanning: boolean;
}

export default function ChecksStream({
  streamedCheckIds,
  allCheckIds,
  checks,
  isScanning,
}: ChecksStreamProps) {
  const getCheckResult = (id: string): CheckResult | undefined =>
    checks?.find(c => c.id === id);

  const isStreamed = (id: string) => streamedCheckIds.includes(id);
  const isPending = (id: string) => !isStreamed(id);
  const isCurrentlyScanning = (id: string) =>
    isScanning && streamedCheckIds.length > 0 && streamedCheckIds[streamedCheckIds.length - 1] === id && !checks;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest font-mono">
          Signal Checks
        </h2>
        {isScanning && (
          <span className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
            <Loader2 size={11} className="animate-spin" aria-hidden="true" />
            {streamedCheckIds.length} / {allCheckIds.length} complete
          </span>
        )}
        {!isScanning && checks && (
          <span className="text-xs font-mono text-muted-foreground">
            {checks.length} / {allCheckIds.length} checks run
          </span>
        )}
      </div>

      <div className="space-y-2">
        {allCheckIds.map((id, index) => {
          const meta = CHECK_META[id];
          if (!meta) return null;

          const IconComponent = meta.icon;
          const result = getCheckResult(id);
          const streamed = isStreamed(id);
          const pending = isPending(id);
          const currentScan = isCurrentlyScanning(id);

          const severity = result?.severity;
          const severityConfig = severity ? SEVERITY_CONFIG[severity] : null;
          const SeverityIcon = severityConfig?.icon;

          return (
            <div
              key={`check-row-${id}`}
              className="relative flex items-center gap-4 px-4 py-3 rounded-xl border transition-all duration-300"
              style={{
                opacity: pending && !currentScan ? 0.35 : 1,
                background: severityConfig ? severityConfig.bg : 'var(--card)',
                borderColor: severityConfig ? severityConfig.border : 'var(--border)',
                animation: streamed && !pending ? `slideInRow 0.4s cubic-bezier(0.16,1,0.3,1) ${index * 0.05}s both` : 'none',
              }}
              aria-label={`${meta.label}: ${result ? result.severity : pending ? 'pending' : 'scanning'}`}
            >
              {/* Icon */}
              <div
                className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                style={{
                  background: severityConfig ? severityConfig.bg : 'var(--muted)',
                  border: `1px solid ${severityConfig ? severityConfig.border : 'var(--border)'}`,
                }}
              >
                <IconComponent
                  size={16}
                  style={{ color: severityConfig ? severityConfig.color : 'var(--muted-foreground)' }}
                  aria-hidden="true"
                />
              </div>

              {/* Label + description */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-sm font-semibold"
                    style={{ color: severityConfig ? severityConfig.color : pending ? 'var(--muted-foreground)' : 'var(--foreground)' }}
                  >
                    {meta.label}
                  </span>
                  {result?.finding && (
                    <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-xs">
                      — {result.finding}
                    </span>
                  )}
                </div>
                {result?.finding && (
                  <p className="text-xs text-muted-foreground mt-0.5 sm:hidden truncate">
                    {result.finding}
                  </p>
                )}
                {!result && !currentScan && (
                  <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                )}
                {currentScan && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="shimmer h-2 w-32 rounded-full" />
                  </div>
                )}
              </div>

              {/* Right side: status + score */}
              <div className="flex-shrink-0 flex items-center gap-3">
                {/* Score */}
                {result && (
                  <span
                    className="text-sm font-bold font-mono tabular-nums hidden sm:block"
                    style={{ color: severityConfig ? severityConfig.color : 'var(--foreground)' }}
                    aria-label={`Score: ${result.score} out of 10`}
                  >
                    {result.score}
                    <span className="text-xs font-normal text-muted-foreground">/10</span>
                  </span>
                )}

                {/* Severity badge */}
                {result && severityConfig && SeverityIcon && (
                  <div
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
                    style={{
                      background: severityConfig.bg,
                      color: severityConfig.color,
                      border: `1px solid ${severityConfig.border}`,
                    }}
                  >
                    <SeverityIcon size={11} aria-hidden="true" />
                    <span className="hidden sm:block">{severityConfig.label}</span>
                  </div>
                )}

                {/* Scanning indicator */}
                {currentScan && (
                  <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                    <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                    <span className="hidden sm:block">Checking…</span>
                  </div>
                )}

                {/* Pending indicator */}
                {pending && !currentScan && (
                  <Clock size={14} className="text-muted-foreground/40" aria-hidden="true" />
                )}
              </div>

              {/* Critical pulse indicator */}
              {result?.severity === 'critical' && (
                <div
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                  style={{
                    background: 'var(--severity-critical)',
                    boxShadow: '0 0 8px var(--severity-critical)',
                  }}
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}